import React, { useState } from 'react';
import { Settings, Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { ListeEmailsModule } from '../../ui/LigneNotification';
import { TokenDef } from '../../ui/TokenPicker';
import {
    SectionEmailConfigurable, ModeleEmail, ValeursEmails,
    valeursVides, valeursDepuisServeur, nombreEteints,
} from '../../ui/SectionEmailConfigurable';
import { moduleApi } from '../../../lib/api';

const TOKENS: TokenDef[] = [
    { token: '{{prenom}}', desc: 'Prénom du convive' },
    { token: '{{evenement}}', desc: "Nom de l'événement" },
    { token: '{{date}}', desc: "Date de l'événement" },
    { token: '{{lien}}', desc: 'Lien vers le choix du repas' },
];
// Pas de {{lien}} pour la clôture — il n'y a plus rien à choisir en ligne à ce stade.
const CLOSURE_TOKENS: TokenDef[] = TOKENS.filter((t) => t.token !== '{{lien}}');

// {{categorie}} n'a de sens que pour cet email (la catégorie du plat de secours proposé) — ajouté à ce
// seul jeu de tokens plutôt qu'au socle commun.
const ALT_DISH_TOKENS: TokenDef[] = [...TOKENS, { token: '{{categorie}}', desc: 'Catégorie concernée (ex: Dessert)' }];

// {{date_limite}} : dernier jour où le convive peut répondre — propre à l'avertissement avant clôture.
const PREWARNING_TOKENS: TokenDef[] = [...TOKENS, { token: '{{date_limite}}', desc: 'Dernier jour pour répondre' }];

/** Les cinq emails de ce module, dans l'ordre où ils partent au fil d'une privatisation. */
const MODELES: ModeleEmail[] = [
    { cle: 'invitation', titre: 'Invitation', tokens: TOKENS, description: "Envoyé au clic sur « Envoyer les invitations »." },
    { cle: 'reminder', titre: 'Relance', tokens: TOKENS, description: "Envoyé à un convive déjà invité qui n'a pas encore répondu." },
    // Avertissement AVANT la fermeture, à ne pas confondre avec la clôture juste en dessous : celui-ci
    // laisse encore au convive le temps de répondre, l'autre constate que c'est trop tard.
    { cle: 'prewarning', titre: 'Avertissement avant clôture', tokens: PREWARNING_TOKENS, description: "Envoyé automatiquement, le matin de la date programmée sur l'événement, aux convives n'ayant pas encore répondu." },
    { cle: 'closure', titre: 'Clôture (non-répondants)', tokens: CLOSURE_TOKENS, description: "Envoyé au clic sur « Clôturer » à tous les convives n'ayant jamais répondu." },
    { cle: 'alt_dish', titre: 'Plat alternatif', tokens: ALT_DISH_TOKENS, description: "Envoyé quand un plat de secours est proposé à un convive n'ayant pu rien choisir dans une catégorie." },
];

/**
 * Réglages des emails envoyés aux convives, au niveau du client — réutilisés pour tous ses événements,
 * comme demandé par Emeni (« bouton paramètre pour personnaliser les emails que les convives reçoivent »).
 *
 * INTERRUPTEURS ajoutés le 2026-09-18 : ce module avait été oublié lors de la mise en place des emails
 * activables, signalé par Emeni. Le serveur refuse d'envoyer un email éteint — un interrupteur que seul
 * l'écran honorerait ne serait qu'un décor.
 */
export function ConviveEmailSettingsModal({ onClose }: { onClose: () => void }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [settings, setSettings] = useState<ValeursEmails>(valeursVides(MODELES));

    React.useEffect(() => {
        moduleApi.getConviveEmailSettings()
            .then((s: any) => setSettings(valeursDepuisServeur(MODELES, s)))
            .catch((e: any) => setError(e?.message || 'Erreur lors du chargement.'))
            .finally(() => setLoading(false));
    }, []);

    const modifier = (patch: ValeursEmails) => setSettings((s) => ({ ...s, ...patch }));

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSaved(false);
        try {
            await moduleApi.updateConviveEmailSettings(settings as any);
            setSaved(true);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    const eteints = nombreEteints(MODELES, settings);

    return (
        <Modal open onClose={onClose} title="Emails des convives" icon={<Settings size={16} />} size="lg"
            subtitle={eteints > 0 ? `${eteints} email${eteints > 1 ? 's' : ''} désactivé${eteints > 1 ? 's' : ''} : il ne partira pas, même si vous cliquez sur le bouton qui le déclenche.` : "Un clic sur un titre ouvre son texte et l'aperçu ; l'interrupteur active ou coupe l'email."}
            footer={<Button variant="outline" onClick={onClose}>Fermer</Button>}>
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
            ) : (
                <div className="space-y-3">
                    <ListeEmailsModule enregistrer={handleSave} etat={saving ? 'en_cours' : error ? 'echec' : saved ? 'fait' : 'repos'}>
                        {MODELES.map((modele) => (
                            <SectionEmailConfigurable key={modele.cle} modele={modele} valeurs={settings} modifier={modifier} />
                        ))}
                    </ListeEmailsModule>
                    {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                </div>
            )}
        </Modal>
    );
}
